import qs from 'qs'
import axios from 'axios'
import request from '@/utils/request'
import { menuCodeHeaders } from '@/utils/menuCodeHeaders'
import {
  FileUploadCompleteReq,
  FileUploadCompleteRes,
  FileUploadPrepareReq,
  FileUploadPrepareRes,
  OrderSearchQuery,
  OrderSearchRes,
  SaveReq,
  SaveRes,
  WorkInvoiceQuery,
  WorkInvoiceRes,
  WorkItemQuery,
  WorkItemRes,
} from './types'
import { FILE_UPLOADS_URL, ORDER_SEARCH_URL, WORK_INVOICES_URL, WORK_ITEMS_URL, WORK_REGISTRATIONS_SAVE_URL } from './endpoints'

/** `giId` 는 반복 파라미터다(`qs` 선례 — `services/return/returnReceipt/apis.ts`). */
const toQuery = <T extends object>(query: T): string =>
  qs.stringify(query, { arrayFormat: 'repeat', skipNulls: true, filter: (_, value) => (value === '' ? undefined : value) })

/** 반송장·원송장·합포 불러오기 조회(3.1). */
export const getWorkInvoices = (query: WorkInvoiceQuery) =>
  request.get<ApiResponseV2<{ items: WorkInvoiceRes[] }>>(`${WORK_INVOICES_URL}?${toQuery(query)}`)

/** 상품바코드·마스터 조회여부 조회(3.2). */
export const getWorkItems = (query: WorkItemQuery) =>
  request.get<ApiResponseV2<{ items: WorkItemRes[] }>>(`${WORK_ITEMS_URL}?${toQuery(query)}`)

/** 저장·검수완료(3.3). */
export const saveWorkRegistrations = (body: SaveReq) =>
  request.post<ApiResponseV2<SaveRes>, SaveReq>(WORK_REGISTRATIONS_SAVE_URL, body)

/** 주문검색 팝업 조회(3.4). */
export const getOrderSearch = (query: OrderSearchQuery) =>
  request.get<ApiResponseV2<{ items: OrderSearchRes[] }>>(`${ORDER_SEARCH_URL}?${toQuery(query)}`)

/** 첨부 업로드 1단계 — 키·presigned PUT URL 발급(3.5). */
export const prepareFileUpload = (body: FileUploadPrepareReq) =>
  request.post<ApiResponseV2<FileUploadPrepareRes>, FileUploadPrepareReq>(FILE_UPLOADS_URL, body, menuCodeHeaders('RGR18'))

/**
 * 첨부 업로드 2단계 — S3 직접 PUT(3.5). `request` 인스턴스를 쓰지 않는다: 그 인스턴스는
 * 요청 인터셉터가 Authorization 을 무조건 얹고(Okta 세션이면 매 요청 갱신) `getOptions` 가
 * `Content-Type` 을 항상 넣는데, presigned URL 서명은 `signedHeaders` 로 지정된 헤더만 실려야
 * 깨지지 않는다. TTL 이 60초라 prepare 직후 곧바로 호출해야 한다(호출부 책임).
 */
export const uploadToPresignedUrl = async (uploadUrl: string, file: Blob, signedHeaders: Record<string, string[]>): Promise<void> => {
  const headers = Object.fromEntries(Object.entries(signedHeaders).map(([name, values]) => [name, values.join(', ')]))
  await axios.put(uploadUrl, file, { headers })
}

/** 첨부 업로드 3단계 — 저장 응답의 `attachmentTargetIds` 에 연결(3.5). */
export const completeFileUpload = (body: FileUploadCompleteReq) =>
  request.patch<ApiResponseV2<FileUploadCompleteRes>, FileUploadCompleteReq>(FILE_UPLOADS_URL, body, menuCodeHeaders('RGR18'))
